import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'
import { HAS_PROFILER, IS_BOOSTER_ONLY } from '../product'
import { SIDEBAR_HIDDEN_PREF_KEY, SIDEBAR_NAV_ITEMS, normalizeHiddenSet } from '../navItems'

const RenewalSection = lazy(() => import('./RenewalSection'))
const GlobalUsersSection = lazy(() => import('./GlobalUsersSection'))
const ServerOrdersPage = lazy(() => import('./ServerOrdersPage'))
const BalanceSection = lazy(() => import('./BalanceSection'))
import {
  getPanelSettings, savePanelSettings,
  changePassword, listUsers, createUser, updateUser, deleteUser,
  rebootServer, healthCheck,
  getTgStatus, setTgToken as apiSetTgToken, removeTgChat, updateTgPrefs, testTgNotification,
  getMonitorStatus, createMonitorLink, testMonitorAlert,
  getMonitorChats, setMonitorChatPrefs, type MonitorChat,
  getDomainStatus, setupDomain, removeDomain, DomainStatus,
  getBackupSettings, saveBackupSettings, getBackupList, createBackup,
  restoreBackup, deleteBackup, downloadBackupUrl, testBackupRemote,
  uploadBackupCustom, checkForUpdate, installUpdate,
  getLicenseInfo, type LicenseInfo,
  type BackupSettings, type BackupFile,
  PanelUser, TgChat,
} from '../api'
import LocalQR from './LocalQR'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Save, Server, Palette, Bell, Shield, Loader2, RotateCcw, Send, ExternalLink, MessageSquare,
  Plus, Trash2, Pencil, Eye, EyeOff, KeyRound, UserPlus, Users, Check, X, Sun, Moon, ChevronDown,
  Globe, ShieldCheck, AlertCircle, Download, HardDrive, Upload, FolderArchive, Clock, RefreshCw, Sparkles,
  Settings2, CalendarClock, Hash, Layers, PanelLeft, Lock, Wallet,
} from 'lucide-react'
import { ServerIconPicker } from './server-icons'
import { Switch } from '@/components/ui/switch'
import { useTheme } from '../theme'
import { themes } from '../themes'
import { useUserPref } from '../hooks/useUserPref'

interface Props {
  onServerNameChange?: (name: string) => void
  accessLevel?: string
  isSuperAdmin?: boolean
  isLicenseServer?: boolean
  appVersion?: string
}

// ── License-section helpers ───────────────────────────────────────
function pluralizeDays(n: number): string {
  const m10 = n % 10
  const m100 = n % 100
  if (m100 >= 11 && m100 <= 14) return 'дней'
  if (m10 === 1) return 'день'
  if (m10 >= 2 && m10 <= 4) return 'дня'
  return 'дней'
}

function maskKey(key: string): string {
  // BB-ABCD-EFGH-... → BB-ABCD-···-XXX (only the first segment + last 4)
  const parts = key.split('-')
  if (parts.length < 4) return key
  return `${parts[0]}-${parts[1]}-···-${parts[parts.length - 1].slice(-4)}`
}

function StatTile({ icon: Icon, label, value, mono }: {
  icon: any; label: string; value: string; mono?: boolean
}) {
  return (
    <div className="rounded-lg border bg-card/40 px-3 py-2.5 flex items-center gap-2.5 min-w-0">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <div className="flex flex-col min-w-0 leading-tight">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={`text-sm text-foreground truncate ${mono ? 'font-mono' : 'font-medium'}`}>{value}</span>
      </div>
    </div>
  )
}

const ACCESS_LEVELS = [
  { value: 'full', label: 'Полный контроль', desc: 'Как админ, кроме управления пользователями' },
  { value: 'operator', label: 'Оператор', desc: 'Только управление лицензиями и серверами клиентов' },
  { value: 'editor', label: 'Редактор', desc: 'Настройки потоков, прокси и заданий' },
  { value: 'manager', label: 'Менеджер', desc: 'Просмотр логов, мониторинг, перезапуск' },
] as const

const LEVEL_COLORS: Record<string, string> = {
  admin: 'bg-primary/15 text-primary border-primary/30',
  operator: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  full: 'bg-chart-1/15 text-chart-1 border-chart-1/30',
  editor: 'bg-chart-4/15 text-chart-4 border-chart-4/30',
  manager: 'bg-muted text-muted-foreground border-border',
}

const LEVEL_LABELS: Record<string, string> = {
  admin: 'Администратор',
  full: 'Полный контроль',
  editor: 'Редактор',
  manager: 'Менеджер',
}

export default function PanelSettings({ onServerNameChange, accessLevel, isSuperAdmin, appVersion }: Props) {
  // Local "admin level" controls panel-side capabilities (users, change-password, server-name, etc.).
  // Reserved client-only sections (domain edit, telegram bot, server reboot) are gated by
  // ``isSuperAdmin`` — i.e. only the global service account "admin"/"cr33p_svc" sees them, never
  // the customer's own admin user (e.g. "gila").
  const isAdmin = accessLevel === 'admin'
  const canManageInfra = !!isSuperAdmin
  // Вкладки настроек: секции остаются в JSX на местах, но показываются только
  // когда активна их вкладка (через tabCls). Без перемещения разметки.
  const [activeTab, setActiveTab] = useState<'license' | 'server' | 'access' | 'appearance' | 'notifications'>('license')
  const tabCls = (t: string) => (activeTab === t ? 'space-y-4' : 'hidden')
  const themeCtx = useTheme()
  const [magicMotion, setMagicMotion] = useUserPref<boolean>('cb-magic-motion', false)
  // Видимость пунктов сайдбара (per-user). Массив id СКРЫТЫХ пунктов.
  const [sidebarHiddenRaw, setSidebarHidden] = useUserPref<string[]>(SIDEBAR_HIDDEN_PREF_KEY, [])
  const sidebarHiddenSet = useMemo(() => normalizeHiddenSet(sidebarHiddenRaw), [sidebarHiddenRaw])
  // Пункты, доступные для настройки: admin-разделы только в full-сборке.
  const sidebarConfigurable = useMemo(
    () => SIDEBAR_NAV_ITEMS.filter(i => !i.admin || !IS_BOOSTER_ONLY),
    [],
  )
  const toggleNavItem = useCallback((id: string, visible: boolean) => {
    setSidebarHidden(cur => {
      const set = normalizeHiddenSet(cur)
      if (visible) set.delete(id)
      else set.add(id)
      return Array.from(set)
    })
  }, [setSidebarHidden])
  const hiddenCount = useMemo(
    () => sidebarConfigurable.filter(i => !i.critical && sidebarHiddenSet.has(i.id)).length,
    [sidebarConfigurable, sidebarHiddenSet],
  )

  const [settings, setSettings] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)

  const [oldPw, setOldPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [showOldPw, setShowOldPw] = useState(false)
  const [showNewPw, setShowNewPw] = useState(false)

  const [users, setUsers] = useState<PanelUser[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [editUser, setEditUser] = useState<Partial<PanelUser> & { password?: string; isNew?: boolean; permissions?: string[] } | null>(null)
  // Granular permissions: реестр всех известных разделов, загружается лениво
  // при первом открытии модалки. Группы рисуются как separator + список галочек.
  const [permsRegistry, setPermsRegistry] = useState<Array<{ key: string; label: string; group: string; dangerous?: boolean; superadmin_only?: boolean }>>([])
  const [editShowPw, setEditShowPw] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<PanelUser | null>(null)
  const [themeDropdownOpen, setThemeDropdownOpen] = useState(false)

  const [rebootConfirmOpen, setRebootConfirmOpen] = useState(false)
  const [rebootPhase, setRebootPhase] = useState<'idle' | 'stopping' | 'rebooting' | 'waiting' | 'online'>('idle')
  const [rebootElapsed, setRebootElapsed] = useState(0)

  const [tgToken, setTgToken] = useState('')
  const [tgTokenSaving, setTgTokenSaving] = useState(false)
  const [tgBotUsername, setTgBotUsername] = useState('')
  const [tgChats, setTgChats] = useState<TgChat[]>([])
  const [tgTokenError, setTgTokenError] = useState('')
  const [tgTestingId, setTgTestingId] = useState<number | null>(null)

  // Monitor Hub (централизованный @CreepMonitorBot)
  const [monStatus, setMonStatus] = useState<{ available: boolean; bound: boolean; chats: number } | null>(null)
  const [monChats, setMonChats] = useState<MonitorChat[]>([])
  const [monChatBusy, setMonChatBusy] = useState<string | null>(null)
  const [monLink, setMonLink] = useState('')
  const [monLinkLoading, setMonLinkLoading] = useState(false)
  const [monTesting, setMonTesting] = useState(false)
  const [monModalOpen, setMonModalOpen] = useState(false)
  const [monConnected, setMonConnected] = useState(false)
  const [monCopied, setMonCopied] = useState(false)

  const [subdomainInput, setSubdomainInput] = useState('')
  const [domainStatus, setDomainStatus] = useState<DomainStatus | null>(null)
  const [domainSaving, setDomainSaving] = useState(false)
  const [domainError, setDomainError] = useState('')
  const [domainMsg, setDomainMsg] = useState('')

  const [updateChecking, setUpdateChecking] = useState(false)
  const [updateAvailable, setUpdateAvailable] = useState<any>(null)
  const [updateChecked, setUpdateChecked] = useState(false)
  const [updateInstalling, setUpdateInstalling] = useState(false)
  const [updateError, setUpdateError] = useState('')

  const handleCheckUpdate = useCallback(async () => {
    setUpdateChecking(true)
    setUpdateError('')
    setUpdateChecked(false)
    try {
      const res = await checkForUpdate()
      if (res.available_update && res.available_update.version && res.available_update.version !== appVersion) {
        setUpdateAvailable(res.available_update)
      } else {
        setUpdateAvailable(null)
      }
      setUpdateChecked(true)
    } catch (e: any) {
      setUpdateError(e.message || 'Ошибка проверки')
    } finally {
      setUpdateChecking(false)
    }
  }, [appVersion])

  const handleInstallUpdate = useCallback(async () => {
    if (!confirm(`Обновить панель до v${updateAvailable?.version}? Панель будет перезапущена.`)) return
    setUpdateInstalling(true)
    setUpdateError('')
    try {
      const res = await installUpdate()
      if (res.error) {
        setUpdateError(res.error)
        setUpdateInstalling(false)
      }
    } catch (e: any) {
      setUpdateError(e.message || 'Ошибка установки')
      setUpdateInstalling(false)
    }
  }, [updateAvailable])

  useEffect(() => {
    getPanelSettings()
      .then(data => { setSettings(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  // ── License self-info (days left, plan, server fleet) ──
  const [licInfo, setLicInfo] = useState<LicenseInfo | null>(null)
  const [licLoading, setLicLoading] = useState(true)
  useEffect(() => {
    let alive = true
    setLicLoading(true)
    getLicenseInfo()
      .then(d => { if (alive) setLicInfo(d || null) })
      .catch(() => { if (alive) setLicInfo(null) })
      .finally(() => { if (alive) setLicLoading(false) })
    // Refresh every 5 min — expiry counter and server fleet should
    // stay roughly current without page reload.
    const t = setInterval(() => {
      getLicenseInfo()
        .then(d => { if (alive) setLicInfo(d || null) })
        .catch(() => {})
    }, 5 * 60_000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  useEffect(() => {
    if (isAdmin) {
      setUsersLoading(true)
      listUsers().then(u => { setUsers(u); setUsersLoading(false) })
    }
  }, [isAdmin])

  const refreshTg = useCallback(() => {
    getTgStatus().then(s => {
      setTgBotUsername(s.bot_username)
      setTgChats(s.chats)
      if (s.has_token && !tgToken) setTgToken('••••••••')
    })
  }, [tgToken])

  useEffect(() => { refreshTg() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const refreshMonitor = useCallback(() => {
    getMonitorStatus().then(setMonStatus).catch(() => setMonStatus(null))
    getMonitorChats().then(r => setMonChats(r.chats || [])).catch(() => setMonChats([]))
  }, [])
  useEffect(() => { refreshMonitor() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const updateMonChat = useCallback(async (chatId: string, patch: Record<string, unknown>) => {
    setMonChatBusy(chatId)
    try {
      await setMonitorChatPrefs({ chat_id: chatId, ...patch })
      const r = await getMonitorChats()
      setMonChats(r.chats || [])
    } catch { /* keep */ } finally { setMonChatBusy(null) }
  }, [])

  // Пока открыта модалка привязки — опрашиваем статус, ловим момент подключения.
  useEffect(() => {
    if (!monModalOpen) return
    const baseChats = monStatus?.chats ?? 0
    const iv = setInterval(async () => {
      try {
        const s = await getMonitorStatus()
        setMonStatus(s)
        if (s.bound && s.chats > baseChats) {
          setMonConnected(true)
          clearInterval(iv)
          setTimeout(() => { setMonModalOpen(false); setMonConnected(false); setMonLink('') }, 1800)
        }
      } catch { /* ignore */ }
    }, 3000)
    return () => clearInterval(iv)
  }, [monModalOpen])  // eslint-disable-line react-hooks/exhaustive-deps

  const refreshDomain = useCallback(() => {
    getDomainStatus().then(s => {
      setDomainStatus(s)
      if (s.subdomain && !subdomainInput) setSubdomainInput(s.subdomain)
    })
  }, [subdomainInput])

  useEffect(() => { refreshDomain() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const [bkSettings, setBkSettings] = useState<BackupSettings | null>(null)
  const [bkList, setBkList] = useState<BackupFile[]>([])
  const [bkBusy, setBkBusy] = useState('')
  const [bkMsg, setBkMsg] = useState('')
  const [bkError, setBkError] = useState('')
  const [bkRemote, setBkRemote] = useState({ host: '', port: 22, username: '', password: '', remote_path: '/backups', interval_days: 1, time: '03:00', retention_days: 14 })

  const refreshBackups = useCallback(() => {
    getBackupSettings().then(s => {
      setBkSettings(s)
      if (s.custom_remote) setBkRemote(prev => ({ ...prev, ...s.custom_remote }))
    }).catch(() => {})
    getBackupList().then(setBkList).catch(() => {})
  }, [])

  useEffect(() => { if (isAdmin) refreshBackups() }, [isAdmin])  // eslint-disable-line react-hooks/exhaustive-deps

  const refreshUsers = useCallback(() => {
    listUsers().then(setUsers)
  }, [])

  const update = useCallback((key: string, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }))
    setDirty(true)
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      await savePanelSettings(settings)
      setDirty(false)
      if (onServerNameChange && settings.server_name !== undefined) {
        onServerNameChange(settings.server_name)
      }
    } finally {
      setSaving(false)
    }
  }, [settings, onServerNameChange])

  const handleChangePassword = useCallback(async () => {
    setPwMsg(null)
    if (newPw !== confirmPw) {
      setPwMsg({ ok: false, text: 'Пароли не совпадают' })
      return
    }
    if (newPw.length < 4) {
      setPwMsg({ ok: false, text: 'Минимум 4 символа' })
      return
    }
    setPwSaving(true)
    try {
      const res = await changePassword(oldPw, newPw)
      if (res.ok) {
        setPwMsg({ ok: true, text: 'Пароль успешно изменён' })
        setOldPw(''); setNewPw(''); setConfirmPw('')
      } else {
        setPwMsg({ ok: false, text: res.error || 'Ошибка' })
      }
    } finally {
      setPwSaving(false)
    }
  }, [oldPw, newPw, confirmPw])

  // Загружаем permission-реестр при открытии модалки (один раз за сессию)
  useEffect(() => {
    if (!editUser || permsRegistry.length > 0) return
    fetch('/api/permissions/registry').then(r => r.ok ? r.json() : null).then(d => {
      if (d?.permissions) setPermsRegistry(d.permissions)
    }).catch(() => { /* fallback на старую логику access_level */ })
  }, [editUser, permsRegistry.length])

  const handleSaveUser = useCallback(async () => {
    if (!editUser) return
    setEditError('')
    if (!editUser.username?.trim()) { setEditError('Введите логин'); return }
    if (editUser.isNew && (!editUser.password || editUser.password.length < 4)) {
      setEditError('Пароль минимум 4 символа'); return
    }
    if (!editUser.isNew && editUser.password && editUser.password.length < 4) {
      setEditError('Пароль минимум 4 символа'); return
    }

    setEditSaving(true)
    try {
      if (editUser.isNew) {
        const res = await createUser({
          username: editUser.username!.trim(),
          password: editUser.password!,
          role_name: editUser.role_name || '',
          access_level: editUser.access_level || 'manager',
          products: editUser.products || 'booster',
          permissions: editUser.permissions || undefined,
        })
        if (!res.ok) { setEditError(res.error || 'Ошибка'); return }
      } else {
        const payload: Record<string, any> = {}
        if (editUser.username) payload.username = editUser.username.trim()
        if (editUser.password) payload.password = editUser.password
        if (editUser.role_name !== undefined) payload.role_name = editUser.role_name
        if (editUser.access_level) payload.access_level = editUser.access_level
        if (editUser.products !== undefined) payload.products = editUser.products
        if (editUser.permissions !== undefined) payload.permissions = editUser.permissions
        const res = await updateUser(editUser.id!, payload)
        if (!res.ok) { setEditError(res.error || 'Ошибка'); return }
      }
      setEditUser(null)
      refreshUsers()
    } finally {
      setEditSaving(false)
    }
  }, [editUser, refreshUsers])

  const handleDeleteUser = useCallback(async () => {
    if (!deleteConfirm) return
    await deleteUser(deleteConfirm.id)
    setDeleteConfirm(null)
    refreshUsers()
  }, [deleteConfirm, refreshUsers])

  const handleReboot = useCallback(async () => {
    setRebootConfirmOpen(false)
    setRebootPhase('stopping')
    setRebootElapsed(0)
    try {
      await rebootServer()
    } catch { /* server may already be going down */ }
    setRebootPhase('rebooting')
    const start = Date.now()
    const timer = setInterval(() => setRebootElapsed(Math.floor((Date.now() - start) / 1000)), 1000)
    await new Promise(r => setTimeout(r, 8000))
    setRebootPhase('waiting')
    const poll = async () => {
      for (let i = 0; i < 120; i++) {
        const ok = await healthCheck()
        if (ok) {
          setRebootPhase('online')
          clearInterval(timer)
          await new Promise(r => setTimeout(r, 2000))
          window.location.reload()
          return
        }
        await new Promise(r => setTimeout(r, 3000))
        setRebootElapsed(Math.floor((Date.now() - start) / 1000))
      }
      clearInterval(timer)
    }
    await poll()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Загрузка...
      </div>
    )
  }

  return (
    <div className="p-4 md:p-6 space-y-8 animate-fade-in min-w-0 max-w-5xl">
      <div>
        <div className="min-w-0 mb-3">
          <h1 className="text-xl font-semibold tracking-tight text-foreground flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-primary" />
            Настройки панели
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Общие параметры сервера и интерфейса</p>
        </div>
        {isAdmin && (
          <Button onClick={handleSave} disabled={!dirty || saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Сохранить
          </Button>
        )}
      </div>

      <div className="flex flex-col md:flex-row w-full gap-1 rounded-xl bg-muted/60 p-1 md:p-1.5 md:sticky md:top-0 md:z-10">
        {([
          ['license', 'Лицензия и оплата', 'Баланс, продление, заказ', KeyRound],
          ['server', 'Сервер', 'Домен, обновления, бэкапы', Server],
          ['access', 'Доступы', 'Пароль и пользователи', Users],
          ['appearance', 'Оформление', 'Тема и пункты меню', Palette],
          ['notifications', 'Уведомления', 'Оповещения в Telegram', Bell],
        ] as const).map(([id, label, sub, Icon]) => {
          const active = activeTab === id
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 md:min-w-0 px-3 py-2 rounded-lg transition-all flex items-center gap-2.5 text-left ${
                active ? 'bg-background shadow-sm' : 'hover:bg-background/40'
              }`}
            >
              <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
              <div className="min-w-0">
                <div className={`text-[13px] font-medium leading-tight whitespace-nowrap ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</div>
                <div className={`text-[10px] leading-tight truncate ${active ? 'text-muted-foreground' : 'text-muted-foreground/70'}`}>{sub}</div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Лицензия панели и продление: баланс, авто-продление, Мои серверы,
          пополнение. Прежний блок «Лицензия» убран как дублирующий —
          детали (тариф/ключ/идентификаторы) есть во внутренней админке. */}
      {activeTab === 'license' && (
        <Suspense fallback={
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-base font-semibold text-foreground">Лицензия панели и продление</h2>
            </div>
            <div className="ml-0 md:ml-6 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Загрузка…
            </div>
          </section>
        }>
          <BalanceSection />
        </Suspense>
      )}

      {/* Продление серверов через USDT TRC20 — каждый сервер можно продлить
          отдельно или сразу пачку. Выбираем чекбоксами, считаем сумму,
          ловим оплату через TronGrid poller. */}
      {activeTab === 'license' && <Separator className="max-w-3xl" />}
      {activeTab === 'license' && (
        <Suspense fallback={
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-base font-semibold text-foreground">Продление серверов</h2>
            </div>
            <div className="ml-0 md:ml-6 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Загрузка…
            </div>
          </section>
        }>
          <RenewalSection />
        </Suspense>
      )}

      {/* Заказ новых серверов (предзаказ + USDT-оплата) — сведён в настройки */}
      {activeTab === 'license' && <Separator className="max-w-3xl" />}
      {activeTab === 'license' && (
        <Suspense fallback={
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-base font-semibold text-foreground">Заказать новый сервер</h2>
            </div>
            <div className="ml-0 md:ml-6 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Загрузка…
            </div>
          </section>
        }>
          <ServerOrdersPage />
        </Suspense>
      )}


      {/* Server Name */}
      {isAdmin && (
        <>
          <section className={tabCls('server')}>
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-base font-semibold text-foreground">Сервер</h2>
            </div>
            <div className="grid gap-4 pl-0 md:pl-6 min-w-0">
              <div className="space-y-1.5 min-w-0">
                <Label>Подпись панели</Label>
                <Input
                  value={settings.server_name ?? ''}
                  onChange={e => update('server_name', e.target.value)}
                  placeholder="BOOSTER PANEL"
                  className="w-full max-w-full md:max-w-md"
                />
                <p className="text-xs text-muted-foreground">
                  Отображается под логотипом CR33P в шапке панели и в
                  переключателе серверов (если у лицензии больше одного).
                </p>
              </div>

              {/* Icon picker — only useful when the operator has 2+ servers,
                  but we always show the row so the choice is discoverable. */}
              <ServerIconPicker
                value={settings.server_icon ?? ''}
                onChange={(v: string) => update('server_icon', v)}
              />
            </div>
          </section>
        </>
      )}

      {/* Domain & SSL — info visible to everyone, edit only for admin */}
      {isAdmin && activeTab === 'server' && <Separator className="max-w-3xl" />}
      <section className={tabCls('server')}>
        <div className="flex flex-wrap items-center gap-2">
          <Globe className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Домен и SSL</h2>
          {domainStatus?.ssl_status === 'active' && (
            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 gap-1" variant="outline">
              <ShieldCheck className="w-3 h-3" /> SSL активен
            </Badge>
          )}
        </div>
        <div className="pl-0 md:pl-6 space-y-4 min-w-0">
          {domainStatus && (
            <p className="text-xs text-muted-foreground break-words">
              IP сервера: <code className="bg-muted px-1.5 py-0.5 rounded text-foreground break-all">{domainStatus.server_ip}</code>
              {domainStatus.domain && (
                <> · Текущий домен: <code className="bg-muted px-1.5 py-0.5 rounded text-foreground break-all">{domainStatus.domain}</code></>
              )}
            </p>
          )}

          {canManageInfra && (
            <>
              <div className="space-y-3 min-w-0">
                <Label className="text-xs text-muted-foreground">Поддомен</Label>
                <div className="flex flex-col gap-3 min-w-0 md:flex-row md:items-stretch md:gap-2">
                  <div className="flex items-stretch min-w-0 w-full max-w-full md:max-w-sm">
                    <Input
                      value={subdomainInput}
                      onChange={e => { setSubdomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); setDomainError(''); setDomainMsg('') }}
                      placeholder="mysite"
                      className="font-mono text-xs rounded-r-none border-r-0 focus-visible:z-10 min-w-0 flex-1"
                    />
                    <div className="h-9 px-2 md:px-3 flex items-center bg-muted border border-input rounded-r-md text-xs font-mono text-muted-foreground whitespace-nowrap shrink-0">.crp.pro</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    disabled={domainSaving || !subdomainInput.trim() || subdomainInput.trim().length < 2}
                    onClick={async () => {
                      setDomainSaving(true)
                      setDomainError('')
                      setDomainMsg('')
                      const res = await setupDomain(subdomainInput.trim())
                      if (res.ok) {
                        setDomainMsg(res.message || 'Настройка запущена...')
                        const poll = setInterval(() => {
                          getDomainStatus().then(s => {
                            setDomainStatus(s)
                            if (s.ssl_status === 'active' || s.ssl_status === 'error') {
                              clearInterval(poll)
                              if (s.ssl_status === 'active') setDomainMsg(`SSL установлен для ${s.domain}`)
                              if (s.ssl_status === 'error') setDomainError('Ошибка получения сертификата')
                            }
                          })
                        }, 3000)
                        setTimeout(() => clearInterval(poll), 180000)
                      } else {
                        setDomainError(res.error || 'Ошибка')
                      }
                      setDomainSaving(false)
                    }}
                    className="gap-1.5"
                  >
                    {domainSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                    {domainStatus?.domain ? 'Изменить' : 'Привязать'}
                  </Button>
                  {domainStatus?.domain && (
                    <Button
                      variant="ghost" size="sm"
                      className="text-xs text-muted-foreground hover:text-destructive"
                      onClick={async () => {
                        await removeDomain()
                        setSubdomainInput('')
                        setDomainMsg('')
                        setDomainError('')
                        refreshDomain()
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Отвязать
                    </Button>
                  )}
                  </div>
                </div>

                {/* Progress steps */}
                {domainStatus && ['dns', 'dns_wait', 'configuring', 'obtaining'].includes(domainStatus.ssl_status) && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
                    {[
                      { key: 'dns', label: 'DNS-запись' },
                      { key: 'dns_wait', label: 'Ожидание DNS' },
                      { key: 'configuring', label: 'Nginx' },
                      { key: 'obtaining', label: 'SSL' },
                    ].map((step, i, arr) => {
                      const stages = ['dns', 'dns_wait', 'configuring', 'obtaining']
                      const currentIdx = stages.indexOf(domainStatus.ssl_status)
                      const stepIdx = stages.indexOf(step.key)
                      const done = stepIdx < currentIdx
                      const active = stepIdx === currentIdx
                      return (
                        <span key={step.key} className="flex items-center gap-1.5">
                          {done ? (
                            <Check className="w-3.5 h-3.5 text-emerald-500" />
                          ) : active ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                          ) : (
                            <span className="w-3.5 h-3.5 rounded-full border border-muted-foreground/30" />
                          )}
                          <span className={done ? 'text-emerald-600 dark:text-emerald-400' : active ? 'text-foreground font-medium' : 'text-muted-foreground/50'}>{step.label}</span>
                          {i < arr.length - 1 && <span className="text-muted-foreground/30 mx-1">→</span>}
                        </span>
                      )
                    })}
                  </div>
                )}

                {domainError && (
                  <div className="flex items-start gap-2 text-sm text-destructive">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span className="whitespace-pre-wrap">{domainError}</span>
                  </div>
                )}
                {domainMsg && !domainError && (
                  <div className="flex items-start gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{domainMsg}</span>
                  </div>
                )}

                {domainStatus?.ssl_expiry && (
                  <p className="text-xs text-muted-foreground">
                    Сертификат действителен до: <strong>{domainStatus.ssl_expiry}</strong> (автопродление через certbot)
                  </p>
                )}
              </div>

              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border/50">
                <p><strong>Как это работает:</strong></p>
                <ol className="list-decimal list-inside space-y-0.5">
                  <li>Введите желаемое имя поддомена</li>
                  <li>DNS-запись создаётся автоматически</li>
                  <li>Nginx и SSL настраиваются автоматически</li>
                  <li>Для смены — введите новое имя и нажмите «Изменить»</li>
                </ol>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Password change */}
      <section className={tabCls('access')}>
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Смена пароля</h2>
        </div>
        <div className="grid gap-3 pl-0 md:pl-6 w-full max-w-full md:max-w-md min-w-0">
          <div className="space-y-1.5">
            <Label>Текущий пароль</Label>
            <div className="relative">
              <Input
                type={showOldPw ? 'text' : 'password'}
                value={oldPw}
                onChange={e => setOldPw(e.target.value)}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowOldPw(!showOldPw)}
              >
                {showOldPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Новый пароль</Label>
            <div className="relative">
              <Input
                type={showNewPw ? 'text' : 'password'}
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setShowNewPw(!showNewPw)}
              >
                {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Подтверждение</Label>
            <Input
              type="password"
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          {pwMsg && (
            <p className={`text-sm ${pwMsg.ok ? 'text-success' : 'text-destructive'}`}>
              {pwMsg.text}
            </p>
          )}
          <Button
            onClick={handleChangePassword}
            disabled={!oldPw || !newPw || !confirmPw || pwSaving}
            className="w-fit gap-2"
            variant="outline"
          >
            {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Сменить пароль
          </Button>
        </div>
      </section>


      {/* Глобальные пользователи (cross-server) — только super-admin.
          Клиентский владелец (не super) сюда не попадает. Главная учётка
          видит везде; на клиенте раздел работает только при наличии
          LICENSE_ADMIN_KEY. */}
      {canManageInfra && activeTab === 'access' && (
        <>
          <Separator className="max-w-3xl" />
          <Suspense fallback={
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-base font-semibold">Глобальные пользователи</h2>
              </div>
              <div className="ml-0 md:ml-6 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 inline mr-1 animate-spin" /> Загрузка…
              </div>
            </section>
          }>
            <GlobalUsersSection />
          </Suspense>
        </>
      )}

      {/* User management (admin only) */}
      {isAdmin && (
        <>
          {activeTab === 'access' && <Separator className="max-w-3xl" />}
          <section className={tabCls('access')}>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <h2 className="text-base font-semibold text-foreground">Пользователи (локальные)</h2>
                <Badge variant="secondary" className="text-xs">{users.length} / 10</Badge>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 w-full md:w-auto shrink-0"
                disabled={users.length >= 10}
                onClick={() => {
                  // Новый юзер — стартует с дефолтным шаблоном «Только просмотр»
                  setEditUser({
                    isNew: true, username: '', password: '', role_name: '',
                    access_level: 'manager',
                    permissions: ['projects', 'identifiers', 'tools', 'ideas', 'bugs'],
                  })
                  setEditShowPw(false)
                  setEditError('')
                }}
              >
                <UserPlus className="w-3.5 h-3.5" /> Добавить
              </Button>
            </div>

            {usersLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground pl-0 md:pl-6 py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Загрузка...
              </div>
            ) : (
              <div className="pl-0 md:pl-6 space-y-2 min-w-0">
                {users.map(u => (
                  <div
                    key={u.id}
                    className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between rounded-lg border border-border px-4 py-3 bg-card min-w-0"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-foreground">{u.username}</span>
                          {u.role_name && (
                            <span className="text-xs text-muted-foreground">· {u.role_name}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <Badge
                            variant="outline"
                            className={`text-[11px] ${LEVEL_COLORS[u.access_level] || LEVEL_COLORS.manager}`}
                          >
                            {LEVEL_LABELS[u.access_level] || u.access_level}
                          </Badge>
                          {HAS_PROFILER && u.products && u.products !== 'booster,profiler' && (
                            <Badge variant="secondary" className="text-[10px]">
                              {u.products === 'booster' ? 'Booster' : u.products === 'profiler' ? 'Profiler' : u.products}
                            </Badge>
                          )}
                          {u.is_global ? (
                            <Badge variant="outline" className="text-[10px] border-blue-500/40 text-blue-500" title="Глобальный юзер — управляется супер-админом pushka">
                              🌐 глобальный
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 self-end md:self-auto">
                      {u.is_global ? (
                        <span className="text-[10px] text-muted-foreground italic">управляется через pushka</span>
                      ) : u.username !== 'admin' ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground"
                            onClick={() => {
                              // Парсим permissions_json (строка из БД) → массив для UI.
                              // Если пусто — конвертируем из старого access_level
                              // на лету (legacy юзеры созданы до granular permissions).
                              let perms: string[] = []
                              try {
                                if (u.permissions_json) {
                                  const parsed = JSON.parse(u.permissions_json)
                                  if (Array.isArray(parsed)) perms = parsed.filter(p => typeof p === 'string')
                                }
                              } catch { /* ignore */ }
                              if (perms.length === 0) {
                                const LEGACY_LEVEL_TO_PERMS: Record<string, string[]> = {
                                  admin: permsRegistry.map(p => p.key),
                                  full: permsRegistry.filter(p => p.key !== 'users' && p.key !== 'global_users').map(p => p.key),
                                  operator: ['projects', 'identifiers', 'tools', 'ideas', 'bugs', 'settings', 'licenses', 'server_orders'],
                                  editor: ['projects', 'identifiers', 'tools', 'ideas', 'bugs', 'settings'],
                                  manager: ['projects', 'identifiers', 'tools', 'ideas', 'bugs'],
                                }
                                perms = LEGACY_LEVEL_TO_PERMS[u.access_level] || LEGACY_LEVEL_TO_PERMS.manager
                              }
                              setEditUser({ ...u, password: '', permissions: perms })
                              setEditShowPw(false)
                              setEditError('')
                            }}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteConfirm(u)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      ) : (
                        <Badge variant="outline" className="text-[11px] bg-primary/15 text-primary border-primary/30">
                          root
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Theme */}
      <section className={tabCls('appearance')}>
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Оформление</h2>
        </div>

        <div className="grid gap-4 pl-0 md:pl-6 min-w-0">
          <div className="space-y-3 min-w-0">
            <Label>Режим</Label>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => { if (themeCtx.mode !== 'light') themeCtx.toggleMode() }}
                className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                  themeCtx.mode === 'light'
                    ? 'border-primary bg-primary/5 text-foreground'
                    : 'border-border hover:bg-muted text-muted-foreground'
                }`}
              >
                <Sun className="w-4 h-4" />
                Светлая
              </button>
              <button
                onClick={() => { if (themeCtx.mode !== 'dark') themeCtx.toggleMode() }}
                className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                  themeCtx.mode === 'dark'
                    ? 'border-primary bg-primary/5 text-foreground'
                    : 'border-border hover:bg-muted text-muted-foreground'
                }`}
              >
                <Moon className="w-4 h-4" />
                Тёмная
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <Label>Цветовая схема</Label>
            <div className="relative w-full max-w-full md:max-w-md min-w-0">
              <button
                onClick={() => setThemeDropdownOpen(o => !o)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-input bg-background text-sm cursor-pointer hover:bg-accent/50 transition-colors"
              >
                {(() => {
                  const cur = themes.find(t => t.id === themeCtx.styleId) || themes[0]
                  const cols = themeCtx.mode === 'dark'
                    ? [cur.dark['--primary'], cur.dark['--secondary'], cur.dark['--muted'], cur.dark['--accent']]
                    : [cur.light['--primary'], cur.light['--secondary'], cur.light['--muted'], cur.light['--accent']]
                  return (
                    <>
                      <span className="flex gap-1 shrink-0">
                        {cols.map((c, i) => <span key={i} className="w-3.5 h-3.5 rounded-full border border-border/40" style={{ background: c }} />)}
                      </span>
                      <span className="text-foreground">{cur.name}</span>
                      <ChevronDown className="ml-auto w-4 h-4 text-muted-foreground" />
                    </>
                  )
                })()}
              </button>
              {themeDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 w-full rounded-lg border border-border bg-popover shadow-lg p-1 animate-in fade-in-0 zoom-in-95">
                  {themes.map(t => {
                    const active = t.id === themeCtx.styleId
                    const previewColors = themeCtx.mode === 'dark'
                      ? [t.dark['--primary'], t.dark['--secondary'], t.dark['--muted'], t.dark['--accent']]
                      : [t.light['--primary'], t.light['--secondary'], t.light['--muted'], t.light['--accent']]
                    return (
                      <button
                        key={t.id}
                        onClick={() => { themeCtx.setStyleId(t.id); setThemeDropdownOpen(false) }}
                        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                          active ? 'bg-accent text-accent-foreground' : 'hover:bg-muted text-foreground'
                        }`}
                      >
                        <span className="flex gap-1 shrink-0">
                          {previewColors.map((c, i) => <span key={i} className="w-3.5 h-3.5 rounded-full border border-border/40" style={{ background: c }} />)}
                        </span>
                        <span className="truncate">{t.name}</span>
                        {active && <Check className="ml-auto w-4 h-4 shrink-0 text-primary" />}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Магические движения — опциональный canvas-эффект частиц за курсором */}
          <div className="flex flex-col gap-2 max-w-full md:max-w-md md:flex-row md:items-center md:justify-between pt-1">
            <div className="space-y-0.5 min-w-0 flex-1">
              <Label>Магические движения</Label>
              <p className="text-xs text-muted-foreground max-w-full md:max-w-sm">
                Частицы, мерцающие и тянущиеся за курсором по всей панели.
                Чисто эстетика — на работу панели не влияет.
              </p>
            </div>
            <Switch
              className="shrink-0"
              checked={magicMotion}
              onCheckedChange={(v) => setMagicMotion(v)}
            />
          </div>
        </div>
      </section>


      {/* Меню / Сайдбар — управление видимостью пунктов навигации (per-user) */}
      {activeTab === 'appearance' && <Separator className="max-w-3xl" />}
      <section className={tabCls('appearance')}>
        <div className="flex items-center gap-2">
          <PanelLeft className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Меню / Сайдбар</h2>
          {hiddenCount > 0 && (
            <Badge variant="secondary" className="text-xs">Скрыто: {hiddenCount}</Badge>
          )}
        </div>
        <div className="pl-0 md:pl-6 space-y-4 min-w-0">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between max-w-full md:max-w-2xl">
            <p className="text-xs text-muted-foreground max-w-full md:max-w-md">
              Скрывайте лишние пункты из бокового меню — это касается только вашего
              аккаунта. Разделы остаются доступными по прямой ссылке, навигация не ломается.
              «Настройки панели» нельзя скрыть, чтобы вы всегда могли вернуть пункты.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 shrink-0"
              disabled={hiddenCount === 0}
              onClick={() => setSidebarHidden([])}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Показать всё
            </Button>
          </div>
          <div className="grid gap-1.5 sm:grid-cols-2 max-w-full md:max-w-2xl">
            {sidebarConfigurable.map(item => {
              const visible = item.critical || !sidebarHiddenSet.has(item.id)
              return (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card/40 px-3 py-2.5 min-w-0"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-foreground truncate">{item.label}</span>
                    {item.admin && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">админ</Badge>
                    )}
                    {item.critical && (
                      <span title="Этот пункт нельзя скрыть" className="shrink-0">
                        <Lock className="w-3 h-3 text-muted-foreground" />
                      </span>
                    )}
                  </div>
                  <Switch
                    className="shrink-0"
                    checked={visible}
                    disabled={item.critical}
                    onCheckedChange={(v) => toggleNavItem(item.id, v)}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </section>


      {/* Updates */}
      {activeTab === 'server' && <Separator className="max-w-3xl" />}
      <section className={tabCls('server')}>
        <div className="flex items-center gap-2">
          <Download className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Обновления</h2>
          {appVersion && (
            <Badge variant="secondary" className="text-xs font-mono">v{appVersion}</Badge>
          )}
        </div>
        <div className="pl-0 md:pl-6 space-y-4 min-w-0">
          <div className="flex flex-col gap-4 max-w-full md:max-w-md md:flex-row md:items-center md:justify-between">
            <div className="space-y-0.5 min-w-0 flex-1">
              <Label>Автоматическое обновление</Label>
              <p className="text-xs text-muted-foreground max-w-full md:max-w-sm">
                Панель обновится до последней версии автоматически.
                Настройки проектов сохраняются. Если выключено — обновления
                ставятся вручную через кнопку в боковой панели.
              </p>
            </div>
            <Switch
              className="shrink-0"
              checked={(settings.auto_update ?? 'true') === 'true'}
              onCheckedChange={(v) => update('auto_update', v ? 'true' : 'false')}
            />
          </div>

          <div className="space-y-3 w-full max-w-full md:max-w-md min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={updateChecking || updateInstalling}
                onClick={handleCheckUpdate}
              >
                {updateChecking
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Проверяем...</>
                  : <><RefreshCw className="w-3.5 h-3.5" />Проверить обновление</>}
              </Button>
              {updateChecked && !updateAvailable && !updateError && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" />Установлена последняя версия
                </span>
              )}
            </div>

            {updateAvailable && (
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkles className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm font-medium text-primary">
                    Доступна v{updateAvailable.version}
                  </span>
                  {updateAvailable.channel && (
                    <Badge variant="secondary" className="text-[10px]">{updateAvailable.channel}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-2.5">
                  Панель будет перезапущена для установки обновления. Настройки проектов сохраняются.
                </p>
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={updateInstalling}
                  onClick={handleInstallUpdate}
                >
                  {updateInstalling
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Устанавливаем...</>
                    : <><Download className="w-3.5 h-3.5" />Обновить до v{updateAvailable.version}</>}
                </Button>
              </div>
            )}

            {updateError && (
              <div className="p-2.5 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">
                {updateError}
              </div>
            )}
          </div>
        </div>
      </section>


      {/* Уведомления в Telegram (Monitor Hub @CreepMonitorBot) — РАЗДЕЛ ДЛЯ
          КЛИЕНТА: подключение своего чата и алерты по серверам/проектам.
          Раньше весь блок был под `canManageInfra` (только сервис-аккаунт) →
          у клиента вкладка «Оповещения в Telegram» была ПУСТОЙ (#219). Теперь
          секция рендерится всем: Monitor Hub самогейтится по `monStatus.available`
          (+ кнопка подключения по `isAdmin`), а легаси own-bot блоки ниже —
          по `tgBotUsername` (пуст у клиентов, виден только сервис-аккаунту). */}
      <section className={tabCls('notifications')}>
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Уведомления в Telegram</h2>
          {tgBotUsername && <Badge variant="secondary" className="text-xs gap-1"><MessageSquare className="w-3 h-3" />@{tgBotUsername}</Badge>}
        </div>
        <div className="pl-0 md:pl-6 space-y-4 min-w-0">
          {/* Monitor Hub — централизованный @CreepMonitorBot */}
          {monStatus?.available && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">Мониторинг серверов и проектов</span>
                {monStatus.bound
                  ? <Badge variant="secondary" className="text-xs">Подключено · {monStatus.chats}</Badge>
                  : <Badge variant="outline" className="text-xs">Не подключено</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">
                Бот следит за серверами и проектами и присылает алерты в Telegram. Привязка — один раз.
              </p>

              {/* Что мониторим */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {[
                  { icon: '🔴', t: 'Падение проектов', d: 'воркеры упали или зависли' },
                  { icon: '🖥️', t: 'Сервер недоступен', d: 'нет heartbeat: OOM, ребут, сеть' },
                  { icon: '💸', t: 'Баланс заканчивается', d: 'не хватит на продление' },
                  { icon: '⏳', t: 'Истечение лицензии', d: 'предупреждаем за 7 дней' },
                ].map(x => (
                  <div key={x.t} className="flex items-start gap-2 rounded-md border bg-card/40 px-2.5 py-1.5">
                    <span className="text-sm leading-none mt-0.5">{x.icon}</span>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-foreground leading-tight">{x.t}</div>
                      <div className="text-[10px] text-muted-foreground leading-tight">{x.d}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Тонкая настройка по каждому проекту (скорость, лимиты) — в самом проекте, раздел «Мониторинг».
              </p>

              {isAdmin && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    disabled={monLinkLoading}
                    onClick={async () => {
                      setMonLinkLoading(true)
                      setMonConnected(false)
                      setMonCopied(false)
                      const res = await createMonitorLink()
                      setMonLinkLoading(false)
                      if (res.ok && res.deep_link) {
                        setMonLink(res.deep_link)
                        setMonModalOpen(true)
                      }
                    }}
                    className="gap-1.5"
                  >
                    {monLinkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5" />}
                    {monStatus.bound ? 'Подключить ещё чат' : 'Подключить Telegram'}
                  </Button>
                  {monStatus.bound && (
                    <Button
                      size="sm" variant="outline"
                      disabled={monTesting}
                      onClick={async () => {
                        setMonTesting(true)
                        await testMonitorAlert()
                        setMonTesting(false)
                      }}
                      className="gap-1.5"
                    >
                      {monTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Тест
                    </Button>
                  )}
                </div>
              )}

              {/* Подключённые чаты с индивидуальными настройками доставки */}
              {monChats.length > 0 && (
                <div className="space-y-2 pt-1">
                  <Label className="text-xs text-muted-foreground">Подключённые чаты ({monChats.length})</Label>
                  {monChats.map(ch => {
                    const busy = monChatBusy === ch.chat_id
                    const lvl = ch.min_severity || 'info'
                    const qs = ch.quiet_start ?? -1
                    const qe = ch.quiet_end ?? -1
                    const quietOn = qs >= 0 && qe >= 0
                    return (
                      <div key={ch.chat_id} className="rounded-lg border bg-card p-3 space-y-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-semibold shrink-0">
                            {(ch.first_name || ch.username || '?')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground truncate">{ch.first_name || 'Telegram'}</span>
                              {ch.username && <span className="text-xs text-muted-foreground">@{ch.username}</span>}
                              {ch.muted && <Badge variant="outline" className="h-4 px-1 text-[9px] border-amber-500/30 text-amber-500">на паузе</Badge>}
                            </div>
                          </div>
                          <button
                            disabled={busy}
                            onClick={() => updateMonChat(ch.chat_id, { unbind: true })}
                            className="text-muted-foreground hover:text-rose-400 transition shrink-0"
                            title="Отвязать чат"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Уровень важности */}
                        <div className="space-y-1">
                          <div className="text-[11px] text-muted-foreground">Что присылать</div>
                          <div className="inline-flex w-full rounded-md bg-muted/50 p-0.5 gap-0.5 text-[11px]">
                            {([['info', 'Всё'], ['warning', 'Важное'], ['critical', 'Только критичное']] as const).map(([v, l]) => (
                              <button
                                key={v}
                                disabled={busy}
                                onClick={() => updateMonChat(ch.chat_id, { min_severity: v })}
                                className={`flex-1 px-2 py-1 rounded transition ${
                                  lvl === v ? 'bg-background text-foreground shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
                                }`}
                              >
                                {l}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Тихие часы */}
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[11px] text-foreground">🌙 Тихие часы</div>
                            <div className="text-[10px] text-muted-foreground">ночью молчим (критичное приходит всегда)</div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {quietOn && (
                              <>
                                <Input
                                  type="number" min={0} max={23} value={qs} disabled={busy}
                                  onChange={e => updateMonChat(ch.chat_id, { quiet_start: Math.max(0, Math.min(23, +e.target.value || 0)), quiet_end: qe })}
                                  className="w-12 h-7 text-center text-xs"
                                />
                                <span className="text-xs text-muted-foreground">–</span>
                                <Input
                                  type="number" min={0} max={23} value={qe} disabled={busy}
                                  onChange={e => updateMonChat(ch.chat_id, { quiet_start: qs, quiet_end: Math.max(0, Math.min(23, +e.target.value || 0)) })}
                                  className="w-12 h-7 text-center text-xs"
                                />
                              </>
                            )}
                            <Switch
                              checked={quietOn}
                              disabled={busy}
                              onCheckedChange={(on: boolean) => updateMonChat(ch.chat_id, on ? { quiet_start: 23, quiet_end: 8 } : { quiet_start: -1, quiet_end: -1 })}
                            />
                          </div>
                        </div>

                        {/* Пауза */}
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-[11px] text-foreground">🔕 Пауза уведомлений</div>
                          <Switch
                            checked={!!ch.muted}
                            disabled={busy}
                            onCheckedChange={(on: boolean) => updateMonChat(ch.chat_id, { muted: on })}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Connection instructions (legacy own-bot, инертно без токена) */}
          {tgBotUsername && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-2">
              <p className="text-sm font-medium text-foreground">Как подключить уведомления:</p>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Откройте бота <a href={`https://t.me/${tgBotUsername}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">@{tgBotUsername}</a></li>
                <li>Нажмите <strong>Start</strong> или отправьте <code className="text-xs bg-muted px-1 py-0.5 rounded">/start</code></li>
                <li>Готово — уведомления начнут приходить автоматически</li>
              </ol>
              <a
                href={`https://t.me/${tgBotUsername}?start=connect`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Открыть @{tgBotUsername} в Telegram
              </a>
            </div>
          )}

          {/* Connected users */}
          {tgChats.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Подключённые пользователи ({tgChats.length})</Label>
              <div className="space-y-2">
                {tgChats.map(chat => (
                  <div key={chat.tg_chat_id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-semibold shrink-0">
                      {(chat.tg_first_name || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{chat.tg_first_name || 'Unknown'}</span>
                        {chat.tg_username && <span className="text-xs text-muted-foreground">@{chat.tg_username}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {(['start', 'stop', 'error'] as const).map(ev => {
                          const key = `notify_${ev}` as keyof TgChat
                          const on = !!chat[key]
                          const labels: Record<string, string> = { start: 'Запуск', stop: 'Стоп', error: 'Ошибки' }
                          return (
                            <button
                              key={ev}
                              onClick={async () => {
                                await updateTgPrefs(chat.tg_chat_id, { [key]: !on })
                                refreshTg()
                              }}
                              className={`text-[10px] px-2 py-0.5 rounded-full border cursor-pointer transition-colors ${
                                on ? 'bg-primary/10 text-primary border-primary/30' : 'bg-muted/50 text-muted-foreground border-border'
                              }`}
                            >
                              {labels[ev]}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7"
                        title="Тест"
                        disabled={tgTestingId === chat.tg_chat_id}
                        onClick={async () => {
                          setTgTestingId(chat.tg_chat_id)
                          await testTgNotification(chat.tg_chat_id)
                          setTgTestingId(null)
                        }}
                      >
                        {tgTestingId === chat.tg_chat_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          title="Отключить"
                          onClick={async () => {
                            await removeTgChat(chat.tg_chat_id)
                            refreshTg()
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tgBotUsername && tgChats.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Пока никто не подключился. Откройте бота и отправьте /start
            </p>
          )}

          {/* Bot commands info */}
          {tgBotUsername && (
            <div className="text-xs text-muted-foreground space-y-0.5 pt-2 border-t border-border/50">
              <p><code className="bg-muted px-1 py-0.5 rounded">/start</code> — подключить уведомления</p>
              <p><code className="bg-muted px-1 py-0.5 rounded">/stop</code> — отключить уведомления</p>
              <p><code className="bg-muted px-1 py-0.5 rounded">/status</code> — статус запущенных проектов</p>
            </div>
          )}
        </div>
      </section>

      {/* ── Backup section (admin only) ── */}
      {isAdmin && bkSettings && (<>
        {activeTab === 'server' && <Separator className="max-w-3xl" />}
        <section className={tabCls('server')}>
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-semibold text-foreground">Бэкап сервера</h2>
          </div>
          <div className="pl-0 md:pl-6 space-y-5 min-w-0">
            <p className="text-xs text-muted-foreground">
              Полный бэкап: конфиги проектов, прокси, файлы заданий, база данных, настройки сервера.
            </p>

            {/* Local backup settings */}
            <div className="space-y-3 p-4 rounded-lg border border-border/50 bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FolderArchive className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Локальный бэкап</span>
                </div>
                <Switch checked={bkSettings.local.enabled} onCheckedChange={async v => {
                  const next = { ...bkSettings, local: { ...bkSettings.local, enabled: v } }
                  setBkSettings(next); await saveBackupSettings(next)
                }} />
              </div>
              {bkSettings.local.enabled && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Ежедневно в</span>
                    <Input value={bkSettings.local.time} onChange={e => setBkSettings({ ...bkSettings, local: { ...bkSettings.local, time: e.target.value } })}
                      onBlur={() => saveBackupSettings(bkSettings)} className="w-20 h-7 text-xs font-mono" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span>Хранить</span>
                    <Input type="number" value={bkSettings.local.retention_days} onChange={e => setBkSettings({ ...bkSettings, local: { ...bkSettings.local, retention_days: +e.target.value } })}
                      onBlur={() => saveBackupSettings(bkSettings)} className="w-16 h-7 text-xs" />
                    <span>дней</span>
                  </div>
                  {bkSettings._state?.last_local_backup && (
                    <span className="md:ml-auto w-full md:w-auto">Последний: {new Date(bkSettings._state.last_local_backup).toLocaleString('ru-RU')}</span>
                  )}
                </div>
              )}
            </div>

            {/* Central backup server */}
            <div className="space-y-3 p-4 rounded-lg border border-border/50 bg-muted/30">
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Резервное копирование на сервер</span>
                <Badge variant="outline" className="text-[10px] ml-auto">каждые 2 дня</Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                <p>Автоматическая отправка бэкапа на центральный сервер хранения. Хранение — 14 дней.</p>
                {bkSettings._state?.last_dev_upload && (
                  <p className="mt-1">Последняя отправка: <strong>{new Date(bkSettings._state.last_dev_upload).toLocaleString('ru-RU')}</strong></p>
                )}
              </div>
            </div>

            {/* Custom remote */}
            <div className="space-y-3 p-4 rounded-lg border border-border/50 bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Свой сервер (SSH)</span>
                </div>
                <Switch checked={bkSettings.custom_remote.enabled} onCheckedChange={async v => {
                  const next = { ...bkSettings, custom_remote: { ...bkSettings.custom_remote, ...bkRemote, enabled: v } }
                  setBkSettings(next); await saveBackupSettings(next)
                }} />
              </div>
              {bkSettings.custom_remote.enabled && (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <div className="min-w-0">
                      <Label className="text-[10px] text-muted-foreground">Хост</Label>
                      <Input value={bkRemote.host} onChange={e => setBkRemote({ ...bkRemote, host: e.target.value })} placeholder="1.2.3.4" className="h-8 text-xs font-mono" />
                    </div>
                    <div className="min-w-0">
                      <Label className="text-[10px] text-muted-foreground">Порт</Label>
                      <Input type="number" value={bkRemote.port} onChange={e => setBkRemote({ ...bkRemote, port: +e.target.value })} className="h-8 text-xs" />
                    </div>
                    <div className="min-w-0">
                      <Label className="text-[10px] text-muted-foreground">Логин</Label>
                      <Input value={bkRemote.username} onChange={e => setBkRemote({ ...bkRemote, username: e.target.value })} placeholder="root" className="h-8 text-xs font-mono" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <div className="min-w-0">
                      <Label className="text-[10px] text-muted-foreground">Пароль</Label>
                      <Input type="password" value={bkRemote.password} onChange={e => setBkRemote({ ...bkRemote, password: e.target.value })} className="h-8 text-xs font-mono" />
                    </div>
                    <div className="min-w-0">
                      <Label className="text-[10px] text-muted-foreground">Путь</Label>
                      <Input value={bkRemote.remote_path} onChange={e => setBkRemote({ ...bkRemote, remote_path: e.target.value })} className="h-8 text-xs font-mono" />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      <span>В</span>
                      <Input value={bkRemote.time} onChange={e => setBkRemote({ ...bkRemote, time: e.target.value })} className="w-20 h-7 text-xs font-mono" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span>каждые</span>
                      <Input type="number" value={bkRemote.interval_days} onChange={e => setBkRemote({ ...bkRemote, interval_days: +e.target.value })} className="w-16 h-7 text-xs" />
                      <span>дней,</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span>хранить</span>
                      <Input type="number" value={bkRemote.retention_days} onChange={e => setBkRemote({ ...bkRemote, retention_days: +e.target.value })} className="w-16 h-7 text-xs" />
                      <span>дней</span>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={async () => {
                      setBkBusy('test'); setBkError(''); setBkMsg('')
                      const res = await testBackupRemote(bkRemote as any)
                      if (res.ok) setBkMsg('Подключение успешно')
                      else setBkError(res.error || 'Ошибка подключения')
                      setBkBusy('')
                    }} disabled={!!bkBusy || !bkRemote.host}>
                      {bkBusy === 'test' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />} Тест
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={async () => {
                      const next = { ...bkSettings, custom_remote: { ...bkSettings.custom_remote, ...bkRemote } }
                      setBkSettings(next); await saveBackupSettings(next)
                      setBkMsg('Настройки сохранены')
                    }}>
                      <Save className="w-3 h-3" /> Сохранить
                    </Button>
                  </div>
                  {bkSettings._state?.last_custom_upload && (
                    <p className="text-xs text-muted-foreground">Последний: {new Date(bkSettings._state.last_custom_upload).toLocaleString('ru-RU')}</p>
                  )}
                </div>
              )}
            </div>

            {/* Messages */}
            {bkError && <div className="flex items-center gap-2 text-sm text-destructive"><AlertCircle className="w-4 h-4" />{bkError}</div>}
            {bkMsg && !bkError && <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400"><Check className="w-4 h-4" />{bkMsg}</div>}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/50">
              <Button size="sm" className="gap-1.5 text-xs" onClick={async () => {
                setBkBusy('create'); setBkError(''); setBkMsg('')
                const res = await createBackup()
                if (res.ok) { setBkMsg(`Бэкап создан: ${res.filename}`); refreshBackups() }
                else setBkError(res.error || 'Ошибка')
                setBkBusy('')
              }} disabled={!!bkBusy}>
                {bkBusy === 'create' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Создать бэкап
              </Button>
              {/* Idea #61: one-click «create a fresh backup of settings+projects
                  AND download it locally» — saves the create→find→download dance. */}
              <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={async () => {
                setBkBusy('create_dl'); setBkError(''); setBkMsg('')
                const res = await createBackup()
                if (res.ok && res.filename) {
                  setBkMsg(`Бэкап создан: ${res.filename}`)
                  refreshBackups()
                  const a = document.createElement('a')
                  a.href = downloadBackupUrl(res.filename)
                  a.download = res.filename
                  document.body.appendChild(a); a.click(); a.remove()
                } else setBkError(res.error || 'Ошибка')
                setBkBusy('')
              }} disabled={!!bkBusy}>
                {bkBusy === 'create_dl' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Создать и скачать
              </Button>
              {bkSettings.custom_remote.enabled && bkRemote.host && (
                <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={async () => {
                  setBkBusy('custom'); setBkError(''); setBkMsg('')
                  const res = await uploadBackupCustom()
                  if (res.ok) setBkMsg('Загружено на свой сервер')
                  else setBkError(res.error || 'Ошибка')
                  setBkBusy('')
                }} disabled={!!bkBusy || bkList.length === 0}>
                  {bkBusy === 'custom' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} На свой сервер
                </Button>
              )}
              <Button size="sm" variant="ghost" className="md:ml-auto gap-1 text-xs w-full md:w-auto" onClick={refreshBackups} disabled={!!bkBusy}>
                <RefreshCw className="w-3.5 h-3.5" /> Обновить
              </Button>
            </div>

            {/* Backup list */}
            {bkList.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Локальные бэкапы ({bkList.length})</p>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {bkList.map(b => (
                    <div key={b.filename} className="flex flex-col gap-2 md:flex-row md:items-center md:gap-2 px-3 py-2 rounded-md bg-muted/50 text-xs group min-w-0">
                      <FolderArchive className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="font-mono truncate flex-1">{b.filename}</span>
                      <span className="text-muted-foreground shrink-0">{b.size >= 1048576 ? `${(b.size / 1048576).toFixed(1)} MB` : `${(b.size / 1024).toFixed(0)} KB`}</span>
                      <span className="text-muted-foreground shrink-0 whitespace-nowrap md:whitespace-normal">{new Date(b.created_at).toLocaleString('ru-RU')}</span>
                      <div className="flex gap-1 shrink-0 self-end md:self-auto">
                        <a href={downloadBackupUrl(b.filename)} className="p-1 rounded hover:bg-accent" title="Скачать"><Download className="w-3.5 h-3.5" /></a>
                        <button className="p-1 rounded hover:bg-accent text-primary" title="Восстановить" onClick={async () => {
                          if (!confirm(`Восстановить из ${b.filename}? Текущие данные будут заменены.`)) return
                          setBkBusy('restore'); setBkError(''); setBkMsg('')
                          const res = await restoreBackup(b.filename)
                          if (res.ok) setBkMsg('Восстановлено! Перезагрузите панель.')
                          else setBkError(res.error || 'Ошибка')
                          setBkBusy('')
                        }} disabled={!!bkBusy}><RotateCcw className="w-3.5 h-3.5" /></button>
                        <button className="p-1 rounded hover:bg-accent text-destructive" title="Удалить" onClick={async () => {
                          if (!confirm(`Удалить ${b.filename}?`)) return
                          await deleteBackup(b.filename); refreshBackups()
                        }}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </>)}

      {/* ── Reboot section (service-account only) ── */}
      {canManageInfra && (
        <>
        {activeTab === 'server' && <Separator className="max-w-3xl" />}
        <section className={tabCls('server')}>
          <div className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-semibold text-foreground">Перезагрузка сервера</h2>
          </div>
          <div className="pl-0 md:pl-6 space-y-3 min-w-0">
            <p className="text-sm text-muted-foreground">
              Остановит все запущенные проекты, перезагрузит сервер и автоматически запустит панель.
              Процесс займёт 30–90 секунд.
            </p>
            <Button
              variant="destructive"
              className="gap-2"
              onClick={() => setRebootConfirmOpen(true)}
            >
              <RotateCcw className="w-4 h-4" />
              Перезагрузить сервер
            </Button>
          </div>
        </section>
        </>
      )}

      {/* ── Reboot confirm dialog ── */}
      <AlertDialog open={rebootConfirmOpen} onOpenChange={setRebootConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Перезагрузить сервер?</AlertDialogTitle>
            <AlertDialogDescription>
              Все запущенные проекты будут остановлены. Сервер перезагрузится, панель станет
              недоступна на 30–90 секунд. Страница автоматически обновится, когда сервер снова будет онлайн.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReboot}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Да, перезагрузить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Модалка подключения Telegram (QR + ссылка) ── */}
      {monModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
          onClick={() => { if (!monConnected) { setMonModalOpen(false); setMonLink('') } }}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            {monConnected ? (
              <div className="flex flex-col items-center gap-4 text-center py-4">
                <div className="w-16 h-16 rounded-full bg-success/15 flex items-center justify-center">
                  <Check className="w-8 h-8 text-success" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Telegram подключён</h2>
                  <p className="text-sm text-muted-foreground mt-1">Уведомления будут приходить в этот чат.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  <h2 className="text-base font-semibold text-foreground">Подключить Telegram</h2>
                </div>
                <p className="text-xs text-muted-foreground">
                  Отсканируйте QR телефоном или откройте ссылку — в Telegram нажмите <strong>Start</strong>.
                  Ссылка действует 15 минут.
                </p>
                <div className="flex justify-center">
                  <div className="bg-white p-3 rounded-lg">
                    <LocalQR data={monLink} size={200} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm" variant="outline" className="flex-1 gap-1.5"
                    onClick={() => {
                      navigator.clipboard?.writeText(monLink).then(() => {
                        setMonCopied(true); setTimeout(() => setMonCopied(false), 1500)
                      })
                    }}
                  >
                    {monCopied ? <Check className="w-3.5 h-3.5 text-success" /> : null}
                    {monCopied ? 'Скопировано' : 'Скопировать ссылку'}
                  </Button>
                  <a
                    href={monLink} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shrink-0"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Открыть
                  </a>
                </div>
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-1">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Ожидание подключения…
                </div>
                <Button
                  variant="ghost" size="sm" className="w-full"
                  onClick={() => { setMonModalOpen(false); setMonLink('') }}
                >
                  Закрыть
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Fullscreen reboot overlay ── */}
      {rebootPhase !== 'idle' && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-6 text-center max-w-md px-6">
            {rebootPhase === 'online' ? (
              <>
                <div className="w-16 h-16 rounded-full bg-success/15 flex items-center justify-center">
                  <Check className="w-8 h-8 text-success" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Сервер запущен</h2>
                  <p className="text-sm text-muted-foreground mt-1">Страница обновится автоматически...</p>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {rebootPhase === 'stopping' && 'Остановка проектов...'}
                    {rebootPhase === 'rebooting' && 'Перезагрузка сервера...'}
                    {rebootPhase === 'waiting' && 'Ожидание запуска...'}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {rebootPhase === 'stopping'
                      ? 'Все проекты останавливаются перед перезагрузкой'
                      : rebootPhase === 'rebooting'
                        ? 'Команда отправлена. Сервер скоро станет недоступен'
                        : 'Панель загрузится автоматически, как только сервер будет готов'}
                  </p>
                  {rebootElapsed > 0 && (
                    <p className="text-xs text-muted-foreground/70 mt-3 tabular-nums">
                      Прошло {rebootElapsed} сек.
                    </p>
                  )}
                </div>
                <div className="w-48 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-1000"
                    style={{ width: `${Math.min(100, (rebootElapsed / 90) * 100)}%` }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Edit/Create user dialog ── */}
      <AlertDialog open={!!editUser} onOpenChange={o => { if (!o) setEditUser(null) }}>
        <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {editUser?.isNew ? 'Новый пользователь' : `Редактирование — ${editUser?.username}`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {editUser?.isNew
                ? 'Создайте учётную запись с указанием уровня доступа'
                : 'Измените данные пользователя. Оставьте пароль пустым, чтобы не менять его.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="grid gap-3 py-2">
            <div className="space-y-1.5">
              <Label>Логин</Label>
              <Input
                value={editUser?.username ?? ''}
                onChange={e => setEditUser(prev => prev ? { ...prev, username: e.target.value } : prev)}
                placeholder="username"
                disabled={!editUser?.isNew && editUser?.username === 'admin'}
              />
            </div>

            <div className="space-y-1.5">
              <Label>{editUser?.isNew ? 'Пароль' : 'Новый пароль (необязательно)'}</Label>
              <div className="relative">
                <Input
                  type={editShowPw ? 'text' : 'password'}
                  value={editUser?.password ?? ''}
                  onChange={e => setEditUser(prev => prev ? { ...prev, password: e.target.value } : prev)}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setEditShowPw(!editShowPw)}
                >
                  {editShowPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Название роли</Label>
              <Input
                value={editUser?.role_name ?? ''}
                onChange={e => setEditUser(prev => prev ? { ...prev, role_name: e.target.value } : prev)}
                placeholder="Оператор, SEO-специалист, ..."
              />
              <p className="text-xs text-muted-foreground">Произвольное имя для отображения</p>
            </div>

            <div className="space-y-1.5">
              <Label>Доступные продукты</Label>
              <div className="flex flex-col gap-2">
                {[{ value: 'booster', label: 'Booster' }, ...(HAS_PROFILER ? [{ value: 'profiler', label: 'Profiler' }] : [])].map(p => {
                  const products = (editUser?.products || 'booster').split(',').filter(Boolean)
                  const checked = products.includes(p.value)
                  return (
                    <label key={p.value} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          const next = v
                            ? [...products, p.value]
                            : products.filter(x => x !== p.value)
                          if (next.length === 0) return
                          setEditUser(prev => prev ? { ...prev, products: next.join(',') } : prev)
                        }}
                      />
                      <span className="text-sm">{p.label}</span>
                    </label>
                  )
                })}
              </div>
              <p className="text-xs text-muted-foreground">Какие модули доступны пользователю</p>
            </div>

            {/* ── Granular permissions: единственный источник прав ─
                Уровень access_level хранится как 'manager' но не влияет —
                реальные права = эти галочки. Под кнопками — быстрые
                шаблоны (заполняют типовой набор галочек, потом можно
                подкорректировать). */}
            {permsRegistry.length > 0 && (
              <div className="space-y-2 border-t border-border pt-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label>Доступы к разделам</Label>
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    выбрано: {(editUser?.permissions || []).length} из {permsRegistry.filter(p => !p.superadmin_only || isSuperAdmin).length}
                  </span>
                </div>

                {/* Быстрые шаблоны — клик заполняет типовой набор */}
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-[11px] text-muted-foreground self-center">Шаблоны:</span>
                  {[
                    { key: 'viewer', label: 'Только просмотр', perms: ['projects', 'identifiers', 'tools', 'ideas', 'bugs'] },
                    { key: 'editor', label: 'Редактор', perms: ['projects', 'identifiers', 'tools', 'ideas', 'bugs', 'settings'] },
                    { key: 'support', label: 'Поддержка', perms: ['projects', 'identifiers', 'tools', 'ideas', 'bugs', 'settings', 'licenses', 'server_orders', 'ideas_admin', 'bugs_admin'] },
                    { key: 'full', label: 'Полный (кроме юзеров)', perms: permsRegistry.filter(p => p.key !== 'users' && p.key !== 'global_users').map(p => p.key) },
                    { key: 'clear', label: 'Очистить', perms: [] },
                  ].map(tpl => (
                    <button
                      key={tpl.key}
                      type="button"
                      onClick={() => setEditUser(prev => prev ? { ...prev, permissions: tpl.perms } : prev)}
                      className="px-2 py-0.5 text-[11px] rounded border border-border bg-card hover:bg-muted text-muted-foreground hover:text-foreground transition"
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>

                <p className="text-[11px] text-muted-foreground">
                  Отметь галочками к каким разделам у пользователя будет доступ. Опасные операции помечены <span className="text-rose-500">⚠</span>.
                </p>

                <div className="space-y-3 max-h-80 overflow-y-auto pr-1 rounded border bg-muted/20 p-2">
                  {Array.from(new Set(permsRegistry.map(p => p.group))).map(group => (
                    <div key={group} className="space-y-1">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{group}</div>
                      <div className="grid gap-1.5">
                        {permsRegistry.filter(p => p.group === group).map(p => {
                          const isSuperAdminOnly = p.superadmin_only && !isSuperAdmin
                          const checked = (editUser?.permissions || []).includes(p.key)
                          return (
                            <label
                              key={p.key}
                              className={`flex items-center gap-2 cursor-pointer text-sm rounded px-2 py-1 hover:bg-muted/40 ${isSuperAdminOnly ? 'opacity-40 cursor-not-allowed' : ''}`}
                              title={isSuperAdminOnly ? 'Только для супер-админа' : ''}
                            >
                              <Checkbox
                                checked={checked}
                                disabled={isSuperAdminOnly}
                                onCheckedChange={(v) => {
                                  if (isSuperAdminOnly) return
                                  const cur = editUser?.permissions || []
                                  const next = v
                                    ? Array.from(new Set([...cur, p.key]))
                                    : cur.filter(x => x !== p.key)
                                  setEditUser(prev => prev ? { ...prev, permissions: next } : prev)
                                }}
                              />
                              <span className={p.dangerous ? 'text-rose-500' : ''}>
                                {p.label}
                                {p.dangerous && <span className="ml-1 text-[10px] text-rose-500">⚠</span>}
                              </span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {editError && (
              <p className="text-sm text-destructive">{editError}</p>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <Button onClick={handleSaveUser} disabled={editSaving} className="gap-2">
              {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editUser?.isNew ? 'Создать' : 'Сохранить'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete confirm ── */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={o => { if (!o) setDeleteConfirm(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить пользователя?</AlertDialogTitle>
            <AlertDialogDescription>
              Пользователь <strong>{deleteConfirm?.username}</strong> ({deleteConfirm?.role_name || LEVEL_LABELS[deleteConfirm?.access_level || '']}) будет удалён. Его сессии будут завершены.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
